import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { loadMediaRecommendationPreferencesFromCookie } from './media-recommendation-preferences-cookie-operations';

import type { MediaOptions } from 'js/src/client';
import { MusicRecommendationModeEnum, WorkspaceMediaModeEnum } from 'js/src/client';
import { storeWorkspaces } from 'js/src/data/account/account-actions';
import Flags from 'js/src/libs/flags';

type MediaRecommendationPreferencesState = MediaOptions & {
    music_recommendation_mode: MusicRecommendationModeEnum;
};

const getSystemDefaults = (): MediaRecommendationPreferencesState => {
    const isDamAvailable = Flags.canSeeDamTab();
    return {
        source_media_enabled: true,
        stock_media_enabled: true,
        stock_icons_enabled: true,
        dam_media_enabled: isDamAvailable,
        workspace_media_mode: WorkspaceMediaModeEnum.OFF,
        workspace_folder_ids: [],
        media_guidance: '',
        minimum_stock_suitability_score: null,
        music_recommendation_mode: MusicRecommendationModeEnum.RECOMMENDED,
    };
};

const getInitialState = (): MediaRecommendationPreferencesState => {
    // Try to load from cookies first
    const cookiePrefs = loadMediaRecommendationPreferencesFromCookie();
    if (cookiePrefs) {
        return {
            ...cookiePrefs,
            music_recommendation_mode:
                cookiePrefs.music_recommendation_mode ?? MusicRecommendationModeEnum.RECOMMENDED,
        };
    }
    // Otherwise use system defaults
    return getSystemDefaults();
};

const initialState: MediaRecommendationPreferencesState = getInitialState();

const mediaRecommendationPreferencesSlice = createSlice({
    name: 'mediaRecommendationPreferences',
    initialState,
    reducers: {
        setSourceMediaEnabled: (state, action: PayloadAction<boolean>) => {
            state.source_media_enabled = action.payload;
        },
        setStockMediaEnabled: (state, action: PayloadAction<boolean>) => {
            state.stock_media_enabled = action.payload;
        },
        setStockIconsEnabled: (state, action: PayloadAction<boolean>) => {
            state.stock_icons_enabled = action.payload;
        },
        setDamMediaEnabled: (state, action: PayloadAction<boolean>) => {
            state.dam_media_enabled = action.payload;
        },
        setWorkspaceMediaMode: (state, action: PayloadAction<WorkspaceMediaModeEnum>) => {
            state.workspace_media_mode = action.payload;
        },
        setWorkspaceFolderIds: (state, action: PayloadAction<number[]>) => {
            state.workspace_folder_ids = action.payload;
        },
        setMediaGuidance: (state, action: PayloadAction<string>) => {
            state.media_guidance = action.payload;
        },
        setMinimumStockSuitabilityScore: (state, action: PayloadAction<number | null>) => {
            state.minimum_stock_suitability_score = action.payload;
        },
        setMusicRecommendationMode: (state, action: PayloadAction<MusicRecommendationModeEnum>) => {
            state.music_recommendation_mode = action.payload;
        },
        setMediaOptions: (state, action: PayloadAction<Partial<MediaOptions>>) => {
            return { ...state, ...action.payload };
        },
    },
    extraReducers: (builder) => {
        // Listen for workspace loaded/updated events to set media_guidance default
        builder.addCase(storeWorkspaces, (state, action) => {
            // When workspaces are loaded, check if we should update media_guidance
            // Only update if current media_guidance is empty (not user-customized)
            if (state.media_guidance === '') {
                const activeWorkspaceId = window.SERVER?.USER?.workspace_id;
                if (activeWorkspaceId) {
                    const activeWorkspace = action.payload.workspaces.find(
                        (ws) => ws.get('id') === activeWorkspaceId,
                    );
                    if (activeWorkspace) {
                        const aiContextEnabled = activeWorkspace.get('ai_context_enabled');
                        const workspaceMediaGuidance = activeWorkspace.get('media_guidance');
                        if (aiContextEnabled === true && workspaceMediaGuidance) {
                            state.media_guidance = workspaceMediaGuidance;
                        }
                    }
                }
            }
        });
    },
});

export const {
    setSourceMediaEnabled,
    setStockMediaEnabled,
    setStockIconsEnabled,
    setDamMediaEnabled,
    setWorkspaceMediaMode,
    setWorkspaceFolderIds,
    setMediaGuidance,
    setMinimumStockSuitabilityScore,
    setMusicRecommendationMode,
    setMediaOptions,
} = mediaRecommendationPreferencesSlice.actions;

export default mediaRecommendationPreferencesSlice.reducer;
